import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, ArrowLeft, Search, FileSpreadsheet, Printer, TrendingUp, TrendingDown, SlidersHorizontal } from 'lucide-react';
import Layout from '../../../components/Layout';
import { inventoryReportService, StockMovementReportDTO } from '../../../services/inventoryReportService';
import { exportToCsv, printReport } from '../../../utils/exportUtils';
import './InventoryReportCommon.css';

const fmtQty  = (n: number) => new Intl.NumberFormat().format(n);
const fmtDate = (d: string) => new Date(d).toLocaleString();
const today   = () => new Date().toISOString().split('T')[0];
const monthAgo = () => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().split('T')[0]; };

const movBadge = (type: string) => {
  if (type === 'IN'  || type === 'GRN' || type === 'Transfer_In')
    return <span className="ims-badge ims-badge-approved">{type}</span>;
  if (type === 'OUT' || type === 'Issue' || type === 'Transfer_Out')
    return <span className="ims-badge ims-badge-rejected">{type}</span>;
  return <span className="ims-badge ims-badge-pending">{type}</span>;
};

const StockMovementLedgerPage: React.FC = () => {
  const navigate  = useNavigate();
  const [startDate,   setStartDate]   = useState(monthAgo());
  const [endDate,     setEndDate]     = useState(today());
  const [hasSearched, setHasSearched] = useState(false);
  const [searching,   setSearching]   = useState(false);
  const [items,       setItems]       = useState<StockMovementReportDTO[]>([]);

  const handleSearch = async () => {
    setSearching(true);
    setHasSearched(true);
    try {
      const res = await inventoryReportService.getMovements({ startDate, endDate });
      const d: StockMovementReportDTO[] = (res.data as any)?.data ?? res.data ?? [];
      setItems(d);
    } catch { setItems([]); }
    finally { setSearching(false); }
  };

  const handleExcel = () => {
    exportToCsv(
      `stock-movements-${startDate}_${endDate}.csv`,
      ['Date', 'Product', 'SKU', 'Warehouse', 'Type', 'Quantity', 'Document', 'Notes', 'By'],
      items.map(i => [
        fmtDate(i.createdAt), i.productName, i.productSKU, i.warehouseName ?? '',
        i.movementType, i.quantity,
        i.relatedDocumentType ? `${i.relatedDocumentType} #${i.relatedDocumentId}` : '',
        i.notes ?? '', i.createdByName ?? '',
      ]),
    );
  };

  const inQty  = items.filter(i => i.quantity > 0).reduce((s, i) => s + i.quantity, 0);
  const outQty = items.filter(i => i.quantity < 0).reduce((s, i) => s + Math.abs(i.quantity), 0);
  const adjQty = items.filter(i => i.movementType === 'Adjustment').reduce((s, i) => s + i.quantity, 0);

  return (
    <Layout>
      <div className="irp-page">

        <div className="irp-hero">
          <div>
            <h1 className="irp-hero-title"><Activity size={22} strokeWidth={1.8} /> Stock Movement Ledger</h1>
            <p className="irp-hero-sub">Full transaction history — IN, OUT, ADJUSTMENT entries with document references</p>
          </div>
          <div className="irp-hero-actions">
            {hasSearched && items.length > 0 && (
              <div className="irp-export-group">
                <button className="irp-export-btn irp-export-excel" onClick={handleExcel}><FileSpreadsheet size={13} /> Export Excel</button>
                <button className="irp-export-btn irp-export-pdf" onClick={printReport}><Printer size={13} /> Print / PDF</button>
              </div>
            )}
            <button className="irp-back-btn" onClick={() => navigate('/reports/inventory')}><ArrowLeft size={14} /> Back</button>
          </div>
        </div>

        <div className="ims-card">
          <div className="ims-card-header">
            <span className="ims-card-title"><Search size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />Date Range Filter</span>
          </div>
          <div className="ims-card-body" style={{ padding: '1rem 1.25rem' }}>
            <div className="irp-filter-bar">
              <div className="irp-filter-group">
                <span className="irp-filter-label">From Date</span>
                <input type="date" className="ims-form-control" value={startDate} onChange={e => setStartDate(e.target.value)} />
              </div>
              <div className="irp-filter-group">
                <span className="irp-filter-label">To Date</span>
                <input type="date" className="ims-form-control" value={endDate} onChange={e => setEndDate(e.target.value)} />
              </div>
              <button className="ims-btn ims-btn-primary" onClick={handleSearch} disabled={searching} style={{ alignSelf: 'flex-end' }}>
                {searching ? <><span className="irp-spinner" /> Searching…</> : <><Search size={14} /> Search</>}
              </button>
            </div>
          </div>
        </div>

        {!hasSearched && (
          <div className="irp-prompt"><Activity size={48} strokeWidth={1} /><p>Set a date range and click <strong>Search</strong>.</p></div>
        )}

        {hasSearched && !searching && (
          <>
            <div className="ims-stats-row" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-total"><Activity size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{items.length}</span>
                  <span className="ims-stat-label">Total Movements</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-value"><TrendingUp size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-positive">{fmtQty(inQty)}</span>
                  <span className="ims-stat-label">Total IN</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-out"><TrendingDown size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value irp-negative">{fmtQty(outQty)}</span>
                  <span className="ims-stat-label">Total OUT</span>
                </div>
              </div>
              <div className="ims-stat-card">
                <div className="ims-stat-icon irp-icon-purple"><SlidersHorizontal size={20} strokeWidth={1.6} /></div>
                <div className="ims-stat-info">
                  <span className="ims-stat-value">{adjQty >= 0 ? '+' : ''}{fmtQty(adjQty)}</span>
                  <span className="ims-stat-label">Adjustments</span>
                </div>
              </div>
            </div>

            {items.length === 0 ? (
              <div className="ims-card"><div className="irp-empty">No movements found for the selected period.</div></div>
            ) : (
              <div className="ims-card">
                <div className="ims-card-header">
                  <span className="ims-card-title">
                    <Activity size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                    Movement Ledger ({items.length} records)
                  </span>
                </div>
                <div className="ims-card-body" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="irp-table">
                    <thead>
                      <tr>
                        <th>Date / Time</th>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Warehouse</th>
                        <th>Type</th>
                        <th className="num">Quantity</th>
                        <th>Document</th>
                        <th>Notes</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(item => (
                        <tr key={item.id}>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)', whiteSpace: 'nowrap' }}>
                            {fmtDate(item.createdAt)}
                          </td>
                          <td style={{ fontWeight: 500 }}>{item.productName}</td>
                          <td><span className="irp-sku">{item.productSKU}</span></td>
                          <td>{item.warehouseName ?? '—'}</td>
                          <td>{movBadge(item.movementType)}</td>
                          <td className={`num ${item.quantity > 0 ? 'irp-positive' : item.quantity < 0 ? 'irp-negative' : ''}`}>
                            {item.quantity > 0 ? `+${fmtQty(item.quantity)}` : fmtQty(item.quantity)}
                          </td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)' }}>
                            {item.relatedDocumentType
                              ? <span className="irp-doc-num">{item.relatedDocumentType} #{item.relatedDocumentId}</span>
                              : '—'}
                          </td>
                          <td style={{ fontSize: '0.78rem', color: 'var(--ims-text-sub)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {item.notes ?? '—'}
                          </td>
                          <td style={{ fontSize: '0.78rem' }}>{item.createdByName ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default StockMovementLedgerPage;
